import { useEffect, useMemo, useState } from "react"

import { Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { RoutesPaths } from "../../../constants/user-routes"
import { useOpenDeflectPage } from "../hooks/useDeflectFilters"
import { DEFLECT_SECTION } from "../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFirstLoad,
  selectDeflectSection
} from "../redux/deflectAnalytics.slice"
import { CHANNEL_COLORS, CHANNEL_COUNT, channelLabel } from "../utils/vocabulary"
import Section from "./Section"
import LineChartSkeleton from "./Skeleton/LineChartSkeleton"
import Toggle from "./Toggle"
import TrendsLineChart from "./TrendsLineChart/TrendsLineChart"

const TOOLTIP_ID = "chartjs-tooltip_deflect_channels"
// TrendsLineChart's canvas height, legend included.
const CHART_HEIGHT = 380
// "Channel trends over time": a line per channel on the page's timeline, by lookup count or by
// volume, the value of the transactions looked up. channel-trends/ carries both, so the toggle
// never asks again. The same card sits on the Channels page, where it has nowhere further to link to.
const ChannelTrendsChart = ({ getData, withViewAll = true }) => {
  const { t } = useTranslation()
  const openPage = useOpenDeflectPage()
  const trends = useSelector(selectDeflectSection(DEFLECT_SECTION.CHANNEL_TRENDS))
  const fetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.CHANNEL_TRENDS))
  const firstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.CHANNEL_TRENDS))
  const [metric, setMetric] = useState("count")

  useEffect(() => {
    getData(DEFLECT_SECTION.CHANNEL_TRENDS)
  }, [getData])

  const series = useMemo(() => {
    const byKey = Object.fromEntries((trends?.series ?? []).map((s) => [s.key, s]))
    const zeros = (trends?.labels ?? []).map(() => 0)

    return Object.keys(CHANNEL_COLORS).map((key) => ({
      key,
      label: t(channelLabel(key)),
      color: CHANNEL_COLORS[key],
      data: byKey[key] ? (metric === "value" ? byKey[key].order_amount : byKey[key].count) : zeros,
      plotted: Boolean(byKey[key])
    }))
  }, [trends, metric, t])

  return (
    <Section
      title={t("Channel trends over time")}
      tooltip={t(
        "One line per channel on the page's timeline. Buckets follow the window: daily up to about a month, weekly up to six months, monthly beyond."
      )}
      extra={
        <Toggle
          value={metric}
          onChange={setMetric}
          options={[
            { value: "count", label: "By count" },
            { value: "value", label: "By volume" }
          ]}
        />
      }
      onViewAll={withViewAll ? () => openPage(RoutesPaths.deflect.analytics_channels) : undefined}
    >
      {firstLoad ? (
        <LineChartSkeleton height={CHART_HEIGHT} legendItems={CHANNEL_COUNT} />
      ) : (
        <Spin spinning={fetching}>
          <TrendsLineChart
            labels={trends?.labels}
            unit={trends?.unit}
            series={series}
            metric={metric}
            tooltipId={TOOLTIP_ID}
          />
        </Spin>
      )}
    </Section>
  )
}

export default ChannelTrendsChart
